/* eslint-disable hubspot-dev/no-private-classes */
'use es6';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import styled from 'styled-components';
import { BATTLESHIP, BUTTON_DISABLED_TEXT, BUTTON_TERTIARY_LIGHT_TEXT, FOCUS_RING_BASE, GYPSUM, KOALA, OLAF } from 'HubStyleTokens/colors';
import { FOCUS_RING_BORDER_ALPHA } from 'HubStyleTokens/sizes';
import { rgba } from 'UIComponents/core/Color';
import UIDropdownCaret from 'UIComponents/dropdown/UIDropdownCaret';
import UISelect from 'UIComponents/input/UISelect';
import { USE_CLASSES as INPUT_USE_CLASSES } from 'UIComponents/input/InputConstants';
import UITextInput from 'UIComponents/input/UITextInput';
import { OptionType } from 'UIComponents/types/OptionTypes';
import { uiFocus } from 'UIComponents/utils/Styles';
import { uniqueId } from 'UIComponents/utils/underscore';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledFontSizeInput = styled(UITextInput).withConfig({
  displayName: "UITextToolbarInputDropdown__StyledFontSizeInput",
  componentId: "sc-19izm9c-0"
})(["&&&{color:", ";cursor:text;font-size:12px !important;height:20px !important;padding:0 !important;width:40px !important;", ";}"], BUTTON_TERTIARY_LIGHT_TEXT, ({
  disabled
}) => disabled && `
      color: ${BUTTON_DISABLED_TEXT} !important;
      cursor: not-allowed;
      `);
const StyledCaret = styled(UIDropdownCaret).withConfig({
  displayName: "UITextToolbarInputDropdown__StyledCaret",
  componentId: "sc-19izm9c-1"
})(["&&&{border-width:4px;}"]);
const StyledContainer = styled.div.withConfig({
  displayName: "UITextToolbarInputDropdown__StyledContainer",
  componentId: "sc-19izm9c-2"
})(["&&&{background-color:", ";border-color:", ";border-radius:3px;border-style:solid;border-width:1px;color:", ";cursor:pointer;display:flex;font-size:12px;padding-bottom:5px;padding-top:5px;transition:all 150ms ease-out;:hover{background-color:", ";}", ";", ";}"], ({
  use
}) => use === 'on-dark' ? OLAF : KOALA, BATTLESHIP, BUTTON_TERTIARY_LIGHT_TEXT, GYPSUM, ({
  open
}) => open && `
      ${uiFocus.join(' ')};
      border-color: ${rgba(FOCUS_RING_BASE, FOCUS_RING_BORDER_ALPHA)};
      outline: 0;
      `, ({
  disabled
}) => disabled && `
      background-color: ${KOALA} !important;
      border-color: ${KOALA} !important;
      color: ${BUTTON_DISABLED_TEXT} !important;
      cursor: not-allowed;
      `);
class UITextToolbarInputDropdown extends PureComponent {
  constructor(props) {
    super(props);
    this.handleFocus = () => {
      this.setState({
        open: true
      });
      this.inputRef.current.focus();
    };
    this.handleBlur = () => {
      this.setState({
        open: false,
        inputValue: null
      });
    };
    this.handleKeyDown = event => {
      if (event.key === 'Enter') {
        this.props.onInputSelect(this.state.inputValue);
        event.preventDefault();
      } else if (event.key === 'Escape') {
        this.inputRef.current.blur();
      }
    };
    this.handleOptionSelect = event => {
      this.props.onOptionSelect(event.target.value);
    };
    this.handleButtonClick = event => {
      if (this.props.disabled || event.target.nodeName === 'INPUT') {
        return;
      }
      if (this.state.open) {
        this.handleBlur();
      } else {
        this.handleFocus();
      }
    };
    this.handleOpenChange = event => {
      if (event.target.value) {
        this.handleFocus();
      } else {
        this.handleBlur();
      }
    };
    this.handleInputValueChange = event => {
      this.setState({
        inputValue: event.target.value
      });
    };
    this.renderInputComponent = inputProps => /*#__PURE__*/_jsxs(StyledContainer, {
      className: "private-button--toolbar",
      disabled: inputProps.disabled,
      onClick: this.handleButtonClick,
      open: this.state.open,
      use: this.props.use,
      children: [/*#__PURE__*/_jsx(StyledFontSizeInput, Object.assign({}, inputProps, {
        styled: false
      })), /*#__PURE__*/_jsx(StyledCaret, {})]
    });
    this.state = {
      open: false,
      inputValue: null
    };
    this._id = uniqueId('text-toolbar-input-');
    this.inputRef = /*#__PURE__*/createRef();
  }
  render() {
    const {
      disabled,
      dropdownClassName,
      options,
      placeholder,
      value
    } = this.props;
    const {
      open,
      inputValue: _inputValue
    } = this.state;
    let inputValue = value || '';
    if (open && typeof _inputValue === 'string') {
      inputValue = _inputValue;
    }
    return /*#__PURE__*/_jsx(UISelect, {
      anchorType: "input",
      autoFocus: open,
      className: "private-button--toolbar",
      closeOnTargetLeave: true,
      disabled: disabled,
      dropdownClassName: classNames('private-typeahead-results--toolbar', dropdownClassName),
      filterOptions: false,
      id: this._id,
      inputComponent: this.renderInputComponent,
      inputRef: this.inputRef,
      inputValue: inputValue,
      value: inputValue,
      minimumSearchCount: Infinity,
      onChange: this.handleOptionSelect,
      onInputKeyDown: this.handleKeyDown,
      onInputValueChange: this.handleInputValueChange,
      onOpenChange: this.handleOpenChange,
      open: open,
      options: options,
      placeholder: placeholder
    });
  }
}
UITextToolbarInputDropdown.propTypes = {
  disabled: PropTypes.bool,
  dropdownClassName: PropTypes.string,
  onInputSelect: PropTypes.func.isRequired,
  onOptionSelect: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(OptionType).isRequired,
  placeholder: PropTypes.string,
  use: PropTypes.oneOf(Object.keys(INPUT_USE_CLASSES)),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
UITextToolbarInputDropdown.displayName = 'UITextToolbarInputDropdown';
export default UITextToolbarInputDropdown;